import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import {
  cartItemUpdate,
  togglePurchasedPrintPackageCartItem,
} from '../../../../../../../services/checkout/redux/checkoutActions';
import { getCheckoutCart } from '../../../../../../../services/checkout/redux/checkoutSelectors';
import AlbumPrintPackageOptionList from './components/AlbumPrintPackageOptionList';
import { PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL } from './components/PurchasedPrintPackageConfigurationCompleteModal';
import usePurchasesPendingConfiguration from './hooks/usePurchasesPendingConfiguration';
import withModal from '../../../../../../../components/global/modal/withModal';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import PhotoPricingOptionList from './components/PhotoPricingOptionList';
import CustomPrintOptionList from './components/CustomPrintOptionList';
import useAlbumMembershipGroupTeamPhotos from '../PurchasingOptions/hooks/useAlbumMembershipGroupTeamPhotos';
import Spinner from '../../../../../../../components/global/Spinner';

export const PURCHASES_PENDING_CONFIGURATION_MODAL =
  'modal/PURCHASES_PENDING_CONFIGURATION';

const propTypes = {
  hierarchyItemId: PropTypes.string.isRequired,
  onCheckout: PropTypes.func.isRequired,
  optionProps: PropTypes.object,
  onRequestClose: PropTypes.func.isRequired,
};

const PurchasesPendingConfigurationModal = ({
  hierarchyItemId,
  onCheckout,
  optionProps,
  actionProps,
  onRequestClose,
}) => {
  const openModal = useOpenModal();
  const { albumMembershipPhoto, identityId, watermarkSettings } =
    actionProps ?? {};

  const {
    hierarchyItem,
    albumMembershipGroup,
    prepaidPrintPackages,
    prepaidPhotoPricings,
    prepaidCustomPrints,
    albumPrintPackages,
    isAllPhotos,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
  });

  const { teamPhotos, loading: loadingTeamPhotos } =
    useAlbumMembershipGroupTeamPhotos({
      albumMembershipGroupId: albumMembershipGroup?.id,
      identityId,
      albumFolderId:
        hierarchyItem?.album_folder?.id ??
        hierarchyItem?.album?.root_album_folder?.id,
    });

  const isTeamPhoto =
    !!albumMembershipPhoto &&
    (teamPhotos ?? []).some(
      teamPhoto => teamPhoto?.photo?.id === albumMembershipPhoto.photo?.id,
    );

  const matchesTeamFilter = name =>
    isTeamPhoto
      ? (name ?? '').toLowerCase().includes('team')
      : !(name ?? '').toLowerCase().includes('team');

  const filteredPrepaidPhotoPricings = (prepaidPhotoPricings ?? []).filter(
    ({
      photo_pricing_print_item: printItem,
      photo_pricing_print_product_item: printProductItem,
    }) => {
      if (printItem) {
        return matchesTeamFilter(printItem?.type?.name);
      }
      if (printProductItem) {
        const ppConfig = printProductItem.print_product_configuration;
        if (isTeamPhoto) {
          return (
            matchesTeamFilter(ppConfig?.print_product?.name ?? '') ||
            matchesTeamFilter(ppConfig?.print_product_size?.print_size?.name)
          );
        }

        return (
          matchesTeamFilter(ppConfig?.print_product?.name ?? '') &&
          matchesTeamFilter(ppConfig?.print_product_size?.print_size?.name)
        );
      }
      return true;
    },
  );

  const onOptionConfigurationComplete = () => {
    openModal(PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL, {
      watermarkSettings,
    });
    onRequestClose();
  };

  if (loadingTeamPhotos) {
    return (
      <div className="text-center my-2">
        <Spinner />
      </div>
    );
  }

  return (
    <Fragment>
      <h4 className="text-center">Add this photo to:</h4>
      <AlbumPrintPackageOptionList
        isAllPhotos={isAllPhotos}
        teamPhotos={teamPhotos}
        albumMembershipGroup={albumMembershipGroup}
        prepaidPrintPackages={prepaidPrintPackages}
        albumPrintPackages={albumPrintPackages}
        albumMembershipPhoto={albumMembershipPhoto}
        hasPurchasedPhotoPricings={filteredPrepaidPhotoPricings?.length > 0}
        onCheckout={onCheckout}
        optionProps={{
          onConfigurationComplete: onOptionConfigurationComplete,
          watermarkSettings,
          teamPhotos,
          ...(optionProps ?? {}),
        }}
      />
      <PhotoPricingOptionList
        isAllPhotos={isAllPhotos}
        teamPhotos={teamPhotos}
        albumMembershipGroup={albumMembershipGroup}
        prepaidPhotoPricings={filteredPrepaidPhotoPricings}
        albumMembershipPhoto={albumMembershipPhoto}
        onCheckout={onCheckout}
        optionProps={{
          onConfigurationComplete: onOptionConfigurationComplete,
          watermarkSettings,
          teamPhotos,
          ...(optionProps ?? {}),
        }}
      />
      <CustomPrintOptionList
        isAllPhotos={isAllPhotos}
        albumMembershipGroup={albumMembershipGroup}
        albumMembershipPhoto={albumMembershipPhoto}
        prepaidCustomPrints={prepaidCustomPrints}
        onCheckout={onCheckout}
        optionProps={{
          onConfigurationComplete: onOptionConfigurationComplete,
          watermarkSettings,
          teamPhotos,
          ...(optionProps ?? {}),
        }}
      />
    </Fragment>
  );
};

PurchasesPendingConfigurationModal.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
});

const mapDispatchToProps = {
  onCartItemUpdate: cartItemUpdate,
  onPurchasedPrintPackageToggle: togglePurchasedPrintPackageCartItem,
};

export default withModal(
  compose(connect(mapStateToProps, mapDispatchToProps))(
    PurchasesPendingConfigurationModal,
  ),
  {
    name: PURCHASES_PENDING_CONFIGURATION_MODAL,
    size: 'sm',
    type: 'full',
  },
);
